<template>
  <v-container>
    <v-row
      v-if="!radio && !$vuetify.breakpoint.xsOnly"
      align="center"
    >
      <v-col
        cols="auto"
      >
        <div class="title text-uppercase font-weight-medium">
          Browse Streams
        </div>
      </v-col>
      <v-spacer />
      <v-col cols="auto">
        <v-text-field
          v-model="filters.description"
          solo
          placeholder="Search"
          flat
          dense
          hide-details
          clearable
          append-icon="fas fa-search"
        />
      </v-col>
    </v-row>

    <v-row class="flex-nowrap">
      <!--<editor-fold desc="Filters">-->
      <filters-column
        v-if="$vuetify.breakpoint.mdAndUp"
        :filters-active="filtersActive"
        @filters:clear-all="clearFilters()"
      >
        <div v-if="!radio && $can('manage-all')">
          <v-subheader class="font-weight-medium">
            Client
          </v-subheader>
          <v-autocomplete
            v-model="filters.client_id"
            solo
            flat
            outlined
            :items="clients.data"
            :loading="clients.loading"
            clearable
            item-text="name"
            item-value="id"
            placeholder="Select client..."
            dense
          />
          <v-subheader class="font-weight-medium">
            Radio
          </v-subheader>
          <v-autocomplete
            v-model="filters.radio_id"
            solo
            flat
            outlined
            :items="radios.data"
            :loading="radios.loading"
            clearable
            item-text="title"
            item-value="id"
            placeholder="Select radio..."
            dense
          />
        </div>
      </filters-column>
      <!--</editor-fold>-->

      <!--<editor-fold desc="Table">-->
      <v-col>
        <pagination
          :page.sync="items.pagination.page"
          :last-page="items.meta.last_page"
          :total="items.meta.total"
          :from="items.meta.from"
          :to="items.meta.to"
          :show-counts="selected.length === 0 && $can('manage-all')"
          :has-actions="selected.length > 0"
          :total-visible="8"
        >
          <template
            v-if="selected.length > 0"
            v-slot:action
          >
            <v-row
              dense
              class="caption"
              align="center"
            >
              <v-col
                cols="auto"
                style="min-width: 130px;"
              >
                <span>With <strong>{{ selected.length }} selected</strong></span>
              </v-col>
              <v-col cols="auto">
                <v-btn
                  outlined
                  class="text-none mr-1"
                  small
                  color="secondary"
                  @click="assignToRadioDialog.visible = true"
                >
                  <v-icon
                    small
                    left
                  >
                    fa fa-plus
                  </v-icon>
                  Assign to Radio
                </v-btn>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      color="secondary"
                      small
                      class="mr-1"
                      :loading="syncing"
                      v-on="on"
                      @click="syncPlayers(selected.map(p => p.id))"
                    >
                      <v-icon small>
                        fa fa-sync
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>Sync streams content</span>
                </v-tooltip>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      color="grey"
                      small
                      v-on="on"
                      @click="deleteConfirmationDialogVisible = true"
                    >
                      <v-icon small>
                        fa fa-trash
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>Delete streams</span>
                </v-tooltip>
              </v-col>
            </v-row>
          </template>
        </pagination>
        <v-data-table
          v-model="selected"
          :headers="headers"
          :items="items.data"
          :server-items-length="items.meta.total"
          :sort-by.sync="items.sorting.sortBy"
          :sort-desc.sync="items.sorting.desc"
          :page.sync="items.pagination.page"
          :items-per-page="items.pagination.perPage"
          :loading="items.loading"
          hide-default-footer
          must-sort
          :mobile-breakpoint="0"
          :show-select="$can('manage-all')"
          class="v-data-table--striped elevation-15"
          @update:sort-by="fetchItems()"
          @update:sort-desc="fetchItems()"
          @update:page="fetchItems()"
        >
          <template v-slot:item="{ item: player, isSelected, select }">
            <tr>
              <td v-if="$can('manage-all')">
                <v-simple-checkbox
                  :value="isSelected"
                  class="show-on-hover"
                  :class="{ 'visible': isSelected }"
                  color="primary"
                  :ripple="false"
                  @input="select"
                />
              </td>
              <td
                v-if="$can('manage-all')"
                style="width: 0px"
                class="text-no-wrap"
              >
                <router-link :to="{ name: 'playerLog', params: { id: player.id }}">
                  <span v-if="player.serial">
                    {{ player.serial }}
                  </span>
                  <span
                    v-else
                    class="grey--text text--lighten-1"
                  >
                    N/A
                  </span>
                </router-link>
              </td>
              <td>
                <router-link :to="{ name: 'playerLog', params: { id: player.id }}">
                  <div
                    v-if="player.stream"
                    class="subtitle-2 primary--text"
                  >
                    {{ player.stream.name }}
                  </div>
                  <div
                    v-else
                    class="subtitle-2 grey--text text--lighten-1"
                  >
                    Unknown
                  </div>
                  <div
                    v-if="player.stream && player.stream.description"
                    class="caption text--secondary"
                  >
                    {{ player.stream.description }}
                  </div>
                </router-link>
              </td>
              <td>
                <router-link :to="{ name: 'playerLog', params: { id: player.id }}">
                  <div v-if="player.stream && player.stream.port">
                    {{ player.stream.port }}
                  </div>
                  <div
                    v-else
                    class="grey--text text--lighten-1"
                  >
                    N/A
                  </div>
                </router-link>
              </td>
              <td
                v-if="$can('manage-all')"
                style="min-width: 170px; max-width: 200px"
                class="text-no-wrap"
              >
                <router-link :to="{ name: 'playerLog', params: { id: player.id }}">
                  <span v-if="player.client">
                    {{ player.client.name }}
                  </span>
                  <span
                    v-else
                    class="grey--text text--lighten-1"
                  >
                    N/A
                  </span>
                </router-link>
              </td>
              <td
                style="min-width: 170px; max-width: 200px"
              >
                <router-link
                  v-if="player.radio"
                  :to="{ name: 'radio', params: { id: player.radio.id }}"
                >
                  <span v-if="player.radio.parent">
                    {{ player.radio.parent.title }} /
                  </span>
                  <span class="secondary--text">
                    {{ player.radio.title }}
                  </span>
                </router-link>
                <router-link
                  v-else
                  :to="{ name: 'playerLog', params: { id: player.id }}"
                >
                  <span class="grey--text text--lighten-1">
                    n/a
                  </span>
                </router-link>
              </td>
              <td style="width: 1px">
                <router-link :to="{ name: 'playerLog', params: { id: player.id }}">
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-chip
                        small
                        :color="player.player_status === PLAYER_STATUS_ONLINE ? 'green darken-1' : 'red darken-1'"
                        text-color="white"
                        dark
                        class="ma-0"
                        v-on="on"
                      >
                        {{ player.player_status === PLAYER_STATUS_ONLINE ? 'Online' : 'Offline' }}
                      </v-chip>
                    </template>
                    <span v-if="player.player_status === PLAYER_STATUS_ONLINE">
                      Status updated <strong>{{ $dayjs(player.player_status_at).from(now) }}</strong>, on {{ $dayjs(player.player_status_at).format('DD.MM.YYYY. HH:mm:ss') }}
                    </span>
                    <span v-else-if="player.player_status === PLAYER_STATUS_OFFLINE">
                      Last online <strong>{{ $dayjs(player.player_last_online_at).from(now) }}</strong>, on {{ $dayjs(player.player_last_online_at).format('DD.MM.YYYY. HH:mm:ss') }}
                    </span>
                  </v-tooltip>
                </router-link>
              </td>
              <td style="width: 1px">
                <router-link :to="{ name: 'playerLog', params: { id: player.id }}">
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-chip
                        small
                        :color="player.stream.service_active ? 'green darken-1' : 'red darken-1'"
                        text-color="white"
                        dark
                        class="ma-0"
                        v-on="on"
                      >
                        {{ player.stream.service_active ? 'Active' : 'Inactive' }}
                      </v-chip>
                    </template>
                    <span>
                      Status updated <strong>{{ $dayjs(player.stream.service_status_at).from(now) }}</strong>, on {{ $dayjs(player.stream.service_status_at).format('DD.MM.YYYY. HH:mm:ss') }}
                    </span>
                  </v-tooltip>
                </router-link>
              </td>
              <td
                class="text-no-wrap"
                style="width: 1px;"
              >
                <span v-if="player.last_sync_at">
                  {{ $dayjs(player.last_sync_at).format('DD.MM.YYYY. HH:mm:ss') }}
                </span>
                <span
                  v-else
                  class="grey--text text--lighten-1"
                >
                  Never
                </span>
              </td>
              <td
                class="text-no-wrap"
                style="width: 1px"
              >
                <v-menu
                  v-if="$can('manage-all')"
                  offset-y
                  min-width="100px"
                >
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      color="grey"
                      small
                      v-on="on"
                    >
                      <v-icon small>
                        fa fa-ellipsis-v
                      </v-icon>
                    </v-btn>
                  </template>
                  <v-list
                    dense
                    class="body-2"
                  >
                    <v-list-item @click="editPlayerDialog.player = player; editPlayerDialog.visible = true">
                      <v-list-item-icon>
                        <v-icon small>
                          fa fa-pen
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>Edit Details</v-list-item-content>
                    </v-list-item>
                    <v-divider />
                    <v-list-item @click="selected = [ player ]; assignToRadioDialog.visible = true">
                      <v-list-item-icon>
                        <v-icon small>
                          fa fa-plus
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>Assign To Radio</v-list-item-content>
                    </v-list-item>
                    <v-list-item @click="syncPlayers([ player.id ])">
                      <v-list-item-icon>
                        <v-icon small>
                          fa fa-sync
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>Sync</v-list-item-content>
                    </v-list-item>
                    <v-list-item @click="restartPlayers([ player.id ], 'both')">
                      <v-list-item-icon>
                        <v-icon small>
                          fa fa-power-off
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>Restart stream</v-list-item-content>
                    </v-list-item>
                    <v-divider />
                    <v-list-item @click="selected = [ player ]; deleteConfirmationDialogVisible = true">
                      <v-list-item-icon>
                        <v-icon small>
                          fa fa-trash
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>Release</v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>
        </v-data-table>
        <div class="pa-2">
          <pagination
            :page.sync="items.pagination.page"
            :last-page="items.meta.last_page"
            :total="items.meta.total"
            :from="items.meta.from"
            :to="items.meta.to"
            :show-counts="$can('manage-all')"
          />
        </div>
      </v-col>
    </v-row>

    <edit-stream-dialog
      :visible.sync="editPlayerDialog.visible"
      :player="editPlayerDialog.player"
      @stream:updated="fetchItems()"
    />

    <assign-to-radio-dialog
      :visible.sync="assignToRadioDialog.visible"
      :player-ids="selected.map(p => p.id)"
      :radios="radios.data"
      @player:assigned="fetchItems()"
    />

    <confirm-delete-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      title="Confirm release"
      text="Are you sure you want to release this stream?"
      button="Yes, release"
      @confirm="releaseStream(selected[0])"
    />
  </v-container>
</template>

<script>
import fetchableItemsMixin from '@/mixins/fetchableItemsMixin'
import FiltersColumn from '@/components/helpers/FiltersColumn'
import Clients from '@/services/Clients'
import { cloneDeep } from 'lodash'
import PlayerModel from '@/models/PlayerModel'
import Players from '@/services/Players'
import Radios from '@/services/Radios'
import AssignToRadioDialog from '@/components/players/AssignToRadioDialog'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog'
import Echo from '@/plugins/echo'
import EditStreamDialog from '@/components/players/EditStreamDialog'
import Streams from '@/services/Streams'

export default {
  name: 'Streams',
  components: {
    EditStreamDialog,
    ConfirmDeleteDialog,
    AssignToRadioDialog,
    FiltersColumn
  },
  mixins: [fetchableItemsMixin, deleteConfirmationMixin],
  props: {
    radio: {
      type: Object,
      default: undefined
    }
  },
  data() {
    return {
      selected: [],
      clients: {
        loading: false,
        data: []
      },
      radios: {
        loading: false,
        data: []
      },
      editPlayerDialog: {
        visible: false,
        player: undefined
      },
      flashDialog: {
        visible: false,
        playerIds: []
      },
      assignToRadioDialog: {
        visible: false,
        radioId: undefined
      },
      deleteConfirmationDialogVisible: false,
      syncing: false,
      rebooting: false,
      flashing: false,
      deleting: false,
      now: new Date()
    }
  },
  watch: {
    'filters.client_id': function () {
      this.fetchRadios()
    }
  },
  beforeCreate() {
    this.emptyFilters = cloneDeep(PlayerModel.emptyFilters)
  },
  created() {
    this.headers = [
      { text: 'Serial', value: 'serial' },
      { text: 'Name', value: 'description' },
      { text: 'Port', value: 'port' },
      ...(this.$can('manage-all') ? [{ text: 'Client', value: 'client_id' }] : []),
      { text: 'Radio', value: 'radio.name' },
      { text: 'Player', value: 'player.status', sortable: false },
      { text: 'Stream', value: 'stream.service_active', sortable: false },
      { text: 'Last Sync', value: 'last_sync_at' },
      { sortable: false }
    ]

    this.items.sorting.sortBy = 'serial'
    this.items.sorting.desc = false

    if (this.radio) {
      this.filters.radio_id = this.radio.id
    }

    this.PLAYER_STATUS_ONLINE = PlayerModel.STATUS_ONLINE
    this.PLAYER_STATUS_OFFLINE = PlayerModel.STATUS_OFFLINE

    this.actionData = PlayerModel.actionData

    this.fetchClients()
    this.fetchRadios()
    this.fetchItems()

    Echo.private('superadmin').listen('StreamPlayerUpdated', (player) => {
      const index = this.items.data.findIndex(p => p.id === player.id)

      if (index === -1) return

      this.items.data.splice(index, 1, player)
    })

    this.interval = setInterval(() => {
      this.now = new Date()
    }, 1000)
  },
  beforeDestroy() {
    Echo.leave('superadmin')

    clearInterval(this.interval)
  },
  methods: {
    async itemsFetcher(queryParams) {
      return await Streams.fetch(queryParams)
    },
    async fetchClients() {
      this.clients.loading = true

      try {
        this.clients.data = (await Clients.fetch())
      } catch (e) {

      } finally {
        this.clients.loading = false
      }
    },
    async fetchRadios() {
      this.radios.loading = true

      try {
        this.radios.data = (await Radios.fetch({
          client_id: this.filters.client_id || undefined
        }))
      } catch (e) {

      } finally {
        this.radios.loading = false
      }
    },
    async syncPlayers(playerIds) {
      this.syncing = true

      await Streams.sync(playerIds)

      this.syncing = false

      this.$store.dispatch('alert', {
        text: 'Successfully sent signal to sync streams',
        color: 'success'
      })
    },
    async restartPlayers(playerIds, type) {
      this.rebooting = true

      await Streams.restart(playerIds, type)

      this.rebooting = false

      this.$store.dispatch('alert', {
        text: 'Successfully sent signal to restart streams',
        color: 'success'
      })
    },
    async assignPlayersToRadio(playerIds, radioId) {
      await Players.assignToRadio(playerIds, radioId, true)

      this.$store.dispatch('alert', {
        text: 'Streans successfuly assigned to radio',
        color: 'success'
      })

      this.fetchItems()
    },
    async releaseStream(player) {
      this.deleting = true

      await Streams.release(player.stream.id)

      this.deleting = false

      this.$store.dispatch('alert', {
        text: 'Stream successfuly released',
        color: 'success'
      })

      this.selected = []

      this.deleteConfirmationDialogVisible = false

      this.fetchItems()
    }
  }
}
</script>
